/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-filename-extension */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment model used to render the dashboards
 *
 * @file Assignment.js
 * @author Satyaki Bose
 */

import { types, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import {
  buildChapterToPageMapping, getPageIdsForChapter, transformAssignmentResponse, getSortedAssignmentsByChapterOrSectionOrder
} from '../utils/AssignmentUtils';
import {
  AET_STORE_EVENTS,
  INSIGHT_BEHAVIOR_TRENDS_QUESTION
} from '../common/Constants';
import AssignmentHelperUtils from '../../../common/utils/AssignmentHelperUtils';
import { Performance } from './StudentAssignments';
import CommonUtils from '../../../common/utils/CommonUtils';

const Tags = types.model({
  bookId: types.optional(types.string, ''),
  chapterId: types.optional(types.string, '')
});

const Progress = types.model({
  assignmentId: types.maybeNull(types.string),
  dueDate: types.maybeNull(types.string),
  totalStudents: types.maybeNull(types.number),
  completedOnTimeCount: types.maybeNull(types.number),
  completedLateCount: types.maybeNull(types.number),
  inProgressCount: types.maybeNull(types.number),
  notStartedCount: types.maybeNull(types.number),
  updatedAt: types.maybeNull(types.string),
  completedOnTimePercentage: types.maybeNull(types.number),
  completedLatePercentage: types.maybeNull(types.number),
  inProgressPercentage: types.maybeNull(types.number),
  notStartedPercentage: types.maybeNull(types.number)
});

const StudentProgress = types.model({
  studentId: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  questionsAttempted: types.maybeNull(types.number),
  questionsAssigned: types.maybeNull(types.number),
  questionsAttemptedOnTime: types.maybeNull(types.number),
  questionsAttemptedLate: types.maybeNull(types.number),
  performance: types.maybeNull(Performance)
});

const StruggleStudent = types.model({
  userId: types.maybeNull(types.string),
  answerConfidence: types.maybeNull(types.string),
  questionsAttempted: types.maybeNull(types.number),
  needsReviewCount: types.maybeNull(types.number),
  gettingThereCount: types.maybeNull(types.number),
  failedFirstAttemptCount: types.maybeNull(types.number),
  manyIncorrectAttemptsCount: types.maybeNull(types.number),
  struggleScore: types.maybeNull(types.number),
  askedAIStudentCount: types.maybeNull(types.number),
  incorrectFirstAttemptRisk: types.maybeNull(types.number),
  multipleIncorrectAttemptsRisk: types.maybeNull(types.number),
  performanceRisk: types.maybeNull(types.number),
  lowConfidenceRisk: types.maybeNull(types.number),
  mediumConfidenceRisk: types.maybeNull(types.number),
  confidenceRisk: types.maybeNull(types.number),
  struggleRisk: types.maybeNull(types.number),
  engagementRisk: types.maybeNull(types.number),
  overallRisk: types.maybeNull(types.number),
  overallStatus: types.maybeNull(types.string)
});

const DifficultQuestionItem = types.model({
  questionId: types.maybeNull(types.string),
  questionStem: types.maybeNull(types.string),
  questionTopic: types.maybeNull(types.string),
  needsReviewCount: types.maybeNull(types.number),
  gettingThereCount: types.maybeNull(types.number),
  failedFirstAttemptCount: types.maybeNull(types.number),
  manyIncorrectAttemptsCount: types.maybeNull(types.number),
  askedAIStudentCount: types.maybeNull(types.number),
  askedEReaderSearchStudentCount: types.maybeNull(types.number),
  difficultyScore: types.maybeNull(types.number),
  studentsWhoAttempted: types.maybeNull(types.number),
  lowConfidenceCount: types.maybeNull(types.number),
  mediumConfidenceCount: types.maybeNull(types.number),
  incorrectFirstAttemptRisk: types.maybeNull(types.number),
  multipleIncorrectAttemptsRisk: types.maybeNull(types.number),
  performanceRisk: types.maybeNull(types.number),
  lowConfidenceRisk: types.maybeNull(types.number),
  mediumConfidenceRisk: types.maybeNull(types.number),
  confidenceRisk: types.maybeNull(types.number),
  questionStrugglingRisk: types.maybeNull(types.number),
  engagementRisk: types.maybeNull(types.number),
  overallDifficultyRisk: types.maybeNull(types.number),
  overallDifficultyStatus: types.maybeNull(types.string)
});

export const Assignment = types.model({
  // New response format fields (primary)
  id: types.string,
  title: types.string,
  assignmentStatus: types.string,
  dateAvailable: types.string,
  dueDate: types.string,
  bookId: types.string,
  assignmentType: types.string,
  createdBy: types.string,
  contentCovered: types.array(types.frozen()),
  assessmentId: types.maybeNull(types.string),

  // Legacy fields (optional for backward compatibility)
  assignmentTags: types.array(types.string),
  assignmentId: types.string,
  learningContextId: types.optional(types.string, ''),
  dateCreated: types.optional(types.string, ''),
  dateModified: types.optional(types.string, ''),
  assignmentDeepLinkId: types.optional(types.string, ''),
  historyId: types.optional(types.string, ''),
  isAssigned: types.optional(types.boolean, false),
  tags: types.optional(Tags, {}),
  syncWithLms: types.optional(types.boolean, false),
  studentMessage: types.optional(types.string, ''),
  origin: types.optional(types.string, ''),
  originalAssignmentId: types.maybeNull(types.string),
  assignableMetaData: types.maybeNull(types.string),
  hasOverride: types.optional(types.boolean, false),
  mcqs: types.optional(types.array(types.frozen()), []),
  dateDue: types.maybeNull(types.string),
  progress: types.maybeNull(Progress),
  studentsProgress: types.optional(types.array(StudentProgress), []),
  struggleStudents: types.optional(types.array(StruggleStudent), []),
  difficultQuestions: types.optional(types.array(DifficultQuestionItem), [])
});

const AssignmentModel = types
  .model({
    assignments: types.optional(types.array(Assignment), []),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string)
  })
  .actions(self => ({
    resetAssignments() {
      self.assignments.clear();
      self.isLoading = false;
      self.error = null;
    },
    setAssignments(assignments) {
      self.assignments = assignments;
      try {
        const assetStore = Framework.getStoreRegistry().getStore('asset');
        const tocData = assetStore ? getSnapshot(assetStore) : null;
        if (tocData && (tocData.children || tocData.slates)) {
          self.updateAssignmentTagsForAllAssignments();
        }
      } catch (e) {
        // Asset store not available yet, tags will be updated when ASSET_FETCHED fires
      }
    },

    addAssignment(assignment) {
      self.assignments.push(assignment);
    },

    updateAssignment(assignmentId, updatedAssignment) {
      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);
      if (index !== -1) {
        self.assignments[index] = updatedAssignment;
      }
    },

    removeAssignment(assignmentId) {
      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);
      if (index !== -1) {
        self.assignments.splice(index, 1);
      }
    },


    /**
     * Student-mode only: after GET /assignments?studentId=<self>, patch dueDate on the
     * already-loaded course list so the learner sees their effective due date instead of
     * the course-level one. Only mutates dueDate on existing rows, never adds new nodes.
     */
    mergeStudentEffectiveDueDates(dueDateByAssignmentId) {
      if (!dueDateByAssignmentId || self.assignments.length === 0) {
        return;
      }
      Object.entries(dueDateByAssignmentId).forEach(([assignmentId, due]) => {
        if (!assignmentId || due == null || String(due).length === 0) {
          return;
        }
        const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);
        if (index !== -1) {
          self.assignments[index].dueDate = String(due);
        }
      });
    },

    setLoading(loading) {
      self.isLoading = loading;
    },

    setError(error) {
      self.error = error;
    },

    clearError() {
      self.error = null;
    },
    populateMCQsForAllAssignments() {
      const mcqAssignments = Framework.getStoreRegistry().getStore('mcqAssignments');
      const mcqStore = getSnapshot(mcqAssignments);
      const asset = Framework.getStoreRegistry().getStore('asset');

      if (mcqStore && mcqStore.mcqQuestions && mcqStore.mcqQuestions.length > 0) {
        // Build the chapter-to-page mapping
        const chapterToPageIdMap = buildChapterToPageMapping(getSnapshot(asset));

        self.assignments.forEach((assignment) => {
          // Check if this is the new format (has contentCovered with detailed structure)
          if (assignment && assignment.contentCovered && assignment.contentCovered.length > 0 && assignment.contentCovered[0].pageId) {
            const allQuestions = assignment.contentCovered
              .filter(content => content && content.questions && Array.isArray(content.questions))
              .flatMap(content => (content.questions.filter(q => q && q.questionId).map(q => ({
                ...q,
                chapterTitle: content.chapterTitle
              }))));

            // Get MCQ details from mcqStore for each questionId and filter out nulls
            assignment.mcqs = allQuestions
              .filter(q => q && q.questionId && mcqStore.mcqQuestions.some(mcq => mcq && mcq.questionId === q.questionId))
              .map((q) => {
                const mcqDetail = mcqStore.mcqQuestions.find(mcq => mcq && mcq.questionId === q.questionId);
                if (!mcqDetail) return null;

                return {
                  chapterTitle: q?.chapterTitle || '',
                  topic: mcqDetail.topic,
                  questionType: mcqDetail.questionType,
                  pageId: mcqDetail.pageId || '',
                  questionId: mcqDetail.questionId || '',
                  questionStem: mcqDetail.questionStem || '',
                  elementId: mcqDetail.elementId || '',
                  relevantText: mcqDetail.relevantText || '',
                  maxAttempts: mcqDetail.maxAttempts || 3,
                  options: mcqDetail.options ? [...mcqDetail.options] : [],
                  answerAttempts: mcqDetail.answerAttempts && Array.isArray(mcqDetail.answerAttempts) ? [...mcqDetail.answerAttempts] : [],
                  point: q.point || 1,
                  assessmentId: q.assessmentId || '',
                  itemId: q.itemId || '',
                  answerConfidence: mcqDetail.answerConfidence,
                  extraMaterialUsed: mcqDetail.extraMaterialUsed
                };
              })
              .filter(mcq => mcq !== null);
          } else {
            // Original format - use existing logic with defensive checks
            const assignmentPageIds = [];
            if (assignment && assignment.tags && assignment.tags.chapterId) {
              const chapterIds = assignment.tags.chapterId.split(',').map(id => id.trim()).filter(id => id);
              chapterIds.forEach((chapterId) => {
                const pageIds = getPageIdsForChapter(chapterId, chapterToPageIdMap);
                if (pageIds && Array.isArray(pageIds)) {
                  assignmentPageIds.push(...pageIds);
                }
              });
            }

            // Filter MCQs that match the assignment's pageIds
            const relevantMCQs = mcqStore.mcqQuestions.filter(mcq => mcq && mcq.pageId && assignmentPageIds.includes(mcq.pageId));

            // Convert MST objects to plain objects to avoid conversion errors
            assignment.mcqs = relevantMCQs.map(mcq => ({
              questionType: mcq.questionType || 'multiple_choice',
              pageId: mcq.pageId || '',
              questionId: mcq.questionId || '',
              questionStem: mcq.questionStem || '',
              elementId: mcq.elementId || '',
              relevantText: mcq.relevantText || '',
              maxAttempts: mcq.maxAttempts || 3,
              options: mcq.options && Array.isArray(mcq.options) ? [...mcq.options] : [],
              answerAttempts: mcq.answerAttempts && Array.isArray(mcq.answerAttempts) ? [...mcq.answerAttempts] : [],
              assessmentId: mcq.assessmentId || ''
            }));
          }
        });

        console.log('Populated MCQs for all assignments using chapter-to-page mapping', getSnapshot(self));
      }
    },
    /**
     * Update assignment tags for all assignments when TOC becomes available
     * This is called when ASSET_FETCHED event fires
     */
    updateAssignmentTagsForAllAssignments() {
      if (self.assignments.length === 0) {
        return;
      }
      let tocData = null;
      try {
        const assetStore = Framework.getStoreRegistry().getStore('asset');
        tocData = assetStore ? getSnapshot(assetStore) : null;
      } catch (e) {
        console.warn('Could not access asset store for updating assignment tags:', e);
        return;
      }
      if (!tocData || (!tocData.children && !tocData.slates)) {
        return;
      }
      self.assignments.forEach((assignment) => {
        const originalAssignment = {
          id: assignment.id,
          title: assignment.title,
          assignmentStatus: assignment.assignmentStatus,
          dateAvailable: assignment.dateAvailable,
          dueDate: assignment.dueDate,
          bookId: assignment.bookId,
          assignmentType: assignment.assignmentType,
          createdBy: assignment.createdBy,
          contentCovered: assignment.contentCovered
        };
        const retransformed = transformAssignmentResponse(originalAssignment);
        assignment.assignmentTags = retransformed.assignmentTags;
      });
    },
    /**
     * Set the progress of an assignment
     *
     * @param {String} assignmentId
     * @param {Object} progress
     * @returns
     */
    setProgressOfAssignment(assignmentId, progress) {
      if (!assignmentId || !progress || self.assignments.length === 0) {
        return;
      }

      const calculatePercentage = (value, total) => {
        if (Number.isNaN(value / total)) {
          return 0;
        }

        return Math.round((value / total) * 100);
      };

      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);

      if (index !== -1) {
        const { difficultQuestions, ...progressData } = progress;
        self.assignments[index].progress = {
          ...progressData,
          completedOnTimePercentage: calculatePercentage(progress?.completedOnTimeCount, progress?.totalStudents),
          completedLatePercentage: calculatePercentage(progress?.completedLateCount, progress?.totalStudents),
          inProgressPercentage: calculatePercentage(progress?.inProgressCount, progress?.totalStudents),
          notStartedPercentage: calculatePercentage(progress?.notStartedCount, progress?.totalStudents)
        };
      }
    },
    /**
     * Set the progress of all assignments
     *
     * @param {Object} data
     * @returns
     */
    setAllAssignmentsProgress(data) {
      // Add defensive checks
      if (!data || !data.assignments || data.assignments.length === 0) {
        return;
      }

      data.assignments.forEach((assignment) => {
        self.setProgressOfAssignment(assignment.assignmentId, assignment);
      });
    },
    /**
     * Set the progress of an assignment
     *
     * @param {String} assignmentId
     * @param {Object} progress
     * @returns
     */
    setStudentsProgressOfAssignment(assignmentId, studentsProgress) {
      if (!assignmentId || !studentsProgress || self.assignments.length === 0 || !studentsProgress.length) {
        return;
      }

      const calculatePercentage = (value, total) => {
        if (Number.isNaN(value / total)) {
          return 0;
        }

        return Math.round((value / total) * 100);
      };

      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);

      if (index !== -1) {
        const studentProgress = studentsProgress.map(student => ({
          ...student,
          questionsAttemptedLate: student?.questionsAttemptedLate || 0,
          questionsAttemptedOnTime: student?.questionsAttemptedOnTime || 0,
          performance: {
            ...(student?.performance || {}),
            greatPercentage: calculatePercentage(
              student?.performance?.great || 0,
              student?.questionsAssigned || 0
            ),
            notAttmptedPercentage: calculatePercentage(
              student?.performance?.notAttmpted || 0,
              student?.questionsAssigned || 0
            ),
            gettingTherePercentage: calculatePercentage(
              student?.performance?.gettingThere || 0,
              student?.questionsAssigned || 0
            ),
            needReviewPercentage: calculatePercentage(
              student?.performance?.needReview || 0,
              student?.questionsAssigned || 0
            )
          }
        }));

        self.assignments[index].studentsProgress = studentProgress;
      }
    },
    setStudentPerformanceOfAssignment(studentProgressSummary = {}) {
      const {
        students = []
      } = studentProgressSummary || {};
      const currentStudentDetails = students?.at(0);
      const {
        assignmentDetails,
        studentId
      } = currentStudentDetails || {};

      // Todo: Move this to a common utility function
      const calculatePercentage = (value, total) => {
        if (Number.isNaN(value / total)) {
          return 0;
        }

        return Math.round((value / total) * 100);
      };

      assignmentDetails.forEach((assignmentDetail) => {
        const {
          assignmentId
        } = assignmentDetail;

        if (!assignmentId) {
          return;
        }

        const index = self.assignments?.findIndex(a => a?.assignmentId === assignmentId);
        if (index !== -1) {
          self.assignments[index].studentsProgress = [{
            studentId,
            status: assignmentDetail?.studentProgress,
            questionsAttempted: assignmentDetail?.attemptedQuestions,
            questionsAssigned: assignmentDetail?.totalQuestions,
            questionsAttemptedLate: assignmentDetail?.questionsAttemptedLate,
            questionsAttemptedOnTime: assignmentDetail?.questionsAttemptedOnTime,
            performance: {
              great: assignmentDetail?.performance?.great,
              notAttmpted: assignmentDetail?.performance?.notAttmpted,
              gettingThere: assignmentDetail?.performance?.gettingThere,
              needReview: assignmentDetail?.performance?.needReview,
              greatPercentage: calculatePercentage(
                assignmentDetail?.performance?.great, assignmentDetail?.totalQuestions
              ),
              notAttmptedPercentage: calculatePercentage(
                assignmentDetail?.performance?.notAttmpted, assignmentDetail?.totalQuestions
              ),
              gettingTherePercentage: calculatePercentage(
                assignmentDetail?.performance?.gettingThere, assignmentDetail?.totalQuestions
              ),
              needReviewPercentage: calculatePercentage(
                assignmentDetail?.performance?.needReview, assignmentDetail?.totalQuestions
              ),
              totalQuestions: assignmentDetail?.totalQuestions
            }
          }];
        }
      });
    },
    /**
     * Fetch the progress of all assignments for a course or a single assignment
     *
     * @param {String} courseId
     * @param {String} assignmentId
     * @returns
     */
    fetchCourseAssignmentProgress(courseId, assignmentId) {
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.COURSE_ASSIGNMENT_PROGRESS_REQUESTED, { courseId, assignmentId }
      );
    },
    /**
     * Fetch the progress of all assignments for a course or a single assignment
     *
     * @param {String} courseId
     * @param {String} assignmentId
     * @returns
     */
    fetchAssignmentAllStudentsProgress(assignmentId) {
      const assignment = self.getAssignmentById(assignmentId);

      if (!assignment || assignment.studentsProgress.length > 0) {
        return;
      }

      Framework.getEventManager().publish(
        AET_STORE_EVENTS.ASSIGNMENT_ALL_STUDENTS_PROGRESS_REQUESTED, { assignmentId }
      );
    },
    setStruggleStudents(assignmentId, students) {
      if (!assignmentId || !students || self.assignments.length === 0) {
        return;
      }
      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);
      if (index !== -1) {
        if (students?.length === 0) {
          return;
        }

        self.assignments[index].struggleStudents = students;
      }
    },

    setDifficultQuestions(assignmentId, questions) {
      if (!assignmentId || !questions || self.assignments.length === 0) {
        return;
      }
      const index = self.assignments.findIndex(a => a.assignmentId === assignmentId);
      if (index !== -1) {
        self.assignments[index].difficultQuestions = questions;
      }
    },
    fetchStruggleStudents(courseId, assignmentId) {
      const assignment = self.getAssignmentById(assignmentId);
      if (!assignment || assignment.struggleStudents.length > 0) {
        return;
      }
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.STRUGGLE_STUDENTS_REQUESTED, { courseId, assignmentId }
      );
    },

    fetchDifficultQuestions(courseId, assignmentId) {
      const assignment = self.getAssignmentById(assignmentId);
      if (!assignment || assignment.difficultQuestions.length > 0) {
        return;
      }
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.DIFFICULT_QUESTIONS_REQUESTED, { courseId, assignmentId }
      );
    },
    getTopDifficultQuestionStems(assignmentId, count) {
      const enriched = self.getDifficultQuestionsWithStems(assignmentId);
      if (enriched.length === 0) return [];
      const sorted = [...enriched].sort((a, b) => {
        const br = Number(b.overallDifficultyRisk ?? 0);
        const ar = Number(a.overallDifficultyRisk ?? 0);
        if (br !== ar) return br - ar;
        const brs = Number(b.questionStrugglingRisk ?? 0);
        const ars = Number(a.questionStrugglingRisk ?? 0);
        if (brs !== ars) return brs - ars;
        return Number(b.performanceRisk ?? 0) - Number(a.performanceRisk ?? 0);
      });
      const sliced = count ? sorted.slice(0, count) : sorted;

      return sliced.map(q => AssignmentHelperUtils.difficultQuestionDisplayStem(
        q.questionStem,
        q.assignmentQuestionNumber
      ));
    }
  }))
  .views(self => ({
    getAssignmentById(assignmentId) {
      const assignments = getSnapshot(self.assignments);
      return assignments.find(assignment => assignment.assignmentId === assignmentId);
    },

    getAssignmentFirstPageId(assignmentId) {
      return self.getAssignmentById(assignmentId)
        ?.contentCovered?.find(content => content.pageId)?.pageId || null;
    },

    getQuestionsByAssignmentId(assignmentId, questionId) {
      return self.getAssignmentById(assignmentId)
        ?.contentCovered?.find(content => content.questions?.find(question => question.questionId === questionId));
    },

    getAssignmentsByBookId(bookId) {
      return self.assignments.filter(assignment => assignment.tags?.bookId === bookId);
    },

    getAssignmentsByStatus(status) {
      return self.assignments.filter(assignment => assignment.assignmentStatus === status);
    },
    getDueThisWeekAssignments() {
      const dueThisWeekAssignments = self.getDueThisWeekAssignmentList();

      return dueThisWeekAssignments?.length > 0
        ? dueThisWeekAssignments
        : self.getUpNextAssignment(); // if no due this week assignments, return the up next assignment
    },
    getDueThisWeekAssignmentList() {
      const assignments = getSnapshot(self.assignments);
      let dueThisWeekAssignments = assignments.filter((assignment) => {
        if (!assignment.dueDate) return false;
        const assignmentDate = new Date(assignment.dueDate);
        const now = new Date();
        // Start of range: today at 00:00:00
        const startOfRange = new Date(now);
        startOfRange.setHours(0, 0, 0, 0);
        // End of range: 6 days from today at 23:59:59
        const endOfRange = new Date(startOfRange);
        endOfRange.setDate(startOfRange.getDate() + 6);
        endOfRange.setHours(23, 59, 59, 999);
        // Include assignments due between today and +6 days
        return assignmentDate >= startOfRange && assignmentDate <= endOfRange;
      }).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate)); // sort by soonest due date first
      // short by Chapter when dueDate is same
      dueThisWeekAssignments = getSortedAssignmentsByChapterOrSectionOrder(dueThisWeekAssignments);

      return dueThisWeekAssignments;
    },
    getUpNextAssignment() {
      let sortedAssignments = self
        .getSortedAssignmentsByDueDate()
        .filter(a => a.dueDate && new Date(a.dueDate) >= new Date());
      sortedAssignments = getSortedAssignmentsByChapterOrSectionOrder(sortedAssignments);

      if (!sortedAssignments || sortedAssignments.length === 0) return [];
      return [sortedAssignments.at(0)];
    },
    getSortedAssignmentsByDueDate() {
      const assignments = [...getSnapshot(self.assignments)];

      return assignments.sort(
        (a, b) => CommonUtils.getComparableTimestamp(a.dueDate) - CommonUtils.getComparableTimestamp(b.dueDate)
      );
    },
    getAssignmentStudentProgress(studentId, assignmentId) {
      if (!studentId || self.assignments.length === 0 || !assignmentId) {
        return null;
      }

      const assignment = self.assignments.find(a => a?.assignmentId === assignmentId);

      if (!assignment) {
        return null;
      }

      const studentProgress = assignment?.studentsProgress?.find(student => student?.studentId === studentId);

      return studentProgress;
    },
    getDefaultPerformance() {
      return {
        great: 0,
        notAttmpted: 0,
        gettingThere: 0,
        needReview: 0,
        greatPercentage: 0,
        notAttmptedPercentage: 0,
        gettingTherePercentage: 0,
        needReviewPercentage: 0,
        totalQuestions: 0
      };
    },
    isAssignmentStartedByAnyStudent(assignmentId) {
      if (!assignmentId) return false;
      const assignment = self.assignments.find(a => a.assignmentId === assignmentId);
      if (!assignment) return false;
      const progress = assignment.progress;
      if (progress && (progress.inProgressCount > 0 || progress.totalStudents !== progress.notStartedCount)) {
        return true;
      }
      return false;
    },
    hasAnyAssignmentStarted() {
      return self.assignments.some((assignment) => {
        if (!assignment) return false;
        const progress = assignment.progress;
        if (progress && (progress.inProgressCount > 0 || progress.totalStudents !== progress.notStartedCount)) {
          return true;
        }
        return false;
      });
    },
    getCountOfAssignedAssignments() {
      return self?.assignments?.length || 0;
    },
    getStudentsList() {
      const studentsStore = Framework.getStoreRegistry().getStore('students');
      return getSnapshot(studentsStore).students;
    },

    resolveStudentName(student, studentsList) {
      const { firstName, lastName } = studentsList?.find(
        s => s?.userId === student?.userId
      ) || {};

      if (!firstName || !lastName) return null;

      return {
        ...student,
        name: `${firstName} ${lastName}`,
        initials: `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`
      };
    },

    // name is derived from the students list
    getStruggleStudentsWithNames(assignmentId, questionSpecificStrugglers = null) {
      const assignment = self.getAssignmentById(assignmentId);
      const struggleStudents = questionSpecificStrugglers != null
        ? questionSpecificStrugglers
        : assignment?.struggleStudents;
      if (!assignment || !struggleStudents?.length) return [];
      const studentsList = self.getStudentsList();

      return struggleStudents
        .map(student => self.resolveStudentName(student, studentsList))
        .filter(Boolean);
    },
    getDifficultQuestionsWithStems(assignmentId) {
      const assignment = self.getAssignmentById(assignmentId);
      if (!assignment || assignment.difficultQuestions.length === 0) return [];

      const mcqStore = Framework.getStoreRegistry().getStore('mcqAssignments');
      const { mcqQuestions } = getSnapshot(mcqStore);

      const withStems = assignment.difficultQuestions.map((dq) => {
        const mcq = mcqQuestions.find(q => q.questionId === dq.questionId);
        return {
          ...dq,
          questionStem: mcq?.questionStem || '',
          questionTopic: mcq?.topic || ''
        };
      });
      return AssignmentHelperUtils.addAssignmentQuestionNumbersToDifficultQuestions(assignment, withStems);
    },
    getBehaviorTrendsFromData(question, rosterTotal = 0) {
      if (!question) return [];

      const language = Framework.getStoreRegistry().getStore('language');
      const attemptedTotal = Number(question.studentsWhoAttempted) || 0;
      const roster = Number(rosterTotal) || 0;

      return INSIGHT_BEHAVIOR_TRENDS_QUESTION.map(({ id, labelKey, countField, totalSource }) => {
        const count = Number(question[countField]) || 0;
        const total = totalSource === 'roster' ? roster : attemptedTotal;

        return {
          id,
          labelKey,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0,
          valueText: AssignmentHelperUtils.formatInsightCountOfStudents(count, total, language),
          description: labelKey
        };
      });
    }
  }))
  .actions(self => ({
    afterCreate() {
      // we need this since sometimes assignment and asset call are in race condition
      Framework.getEventManager().on(AET_STORE_EVENTS.ASSET_FETCHED, () => {
        self.updateAssignmentTagsForAllAssignments();
      });
    }
  }));

export default AssignmentModel;
